/**
 * Activities sidebar + view-setup need “where is the API collection?” from session/schedule payloads.
 * GET /genpt/sessions/:id and schedule rows disagree (snake/camel, root vs nested) — normalize here
 * so Activities.js does not carry a wall of field checks.
 */

function baseHasFiles(base) {
  return (
    (Array.isArray(base.api_files) && base.api_files.length > 0) ||
    (Array.isArray(base.files) && base.files.length > 0) ||
    !!(base.file_key || base.collection_s3_filename)
  );
}

function baseHasUrls(base) {
  if (Array.isArray(base.api_collection_urls) && base.api_collection_urls.length > 0) return true;
  return !!String(base.api_collection_url || base.apiCollectionUrl || base.collection_url || '').trim();
}

function clearUrlFields(base) {
  delete base.api_collection_urls;
  delete base.api_collection_url;
  delete base.apiCollectionUrl;
  delete base.collection_url;
}

function clearFileFields(base) {
  delete base.api_files;
  delete base.files;
  delete base.file_key;
  delete base.api_file_key;
  delete base.collection_s3_filename;
}

/**
 * Merge one source into the aggregate `api_config`-like blob.
 * Enforces **one collection modality**: URLs vs files — never both (session file must not pick up stale schedule URLs).
 */
function mergeInto(base, part) {
  if (!base || !part || typeof base !== 'object' || typeof part !== 'object') return;

  const f = [part.api_files, part.apiFiles].find((a) => Array.isArray(a) && a.length > 0);
  const legacyFiles = Array.isArray(part.files) && part.files.length > 0 ? part.files : null;
  const fk = part.file_key || part.api_file_key || part.collection_s3_filename || null;

  const urlArr = [part.api_collection_urls, part.apiCollectionUrls].find(
    (a) => Array.isArray(a) && a.length > 0
  );
  const urlOne = String(part.api_collection_url || part.apiCollectionUrl || '').trim();
  const curl = part.collection_url || part.api_collection_url;
  const collectionUrlLegacy = curl && String(curl).trim() !== '' ? String(curl).trim() : '';

  let partHasFiles = !!(f || legacyFiles || fk);
  let partHasUrls = !!(urlArr?.length || urlOne || collectionUrlLegacy);
  if (partHasFiles && partHasUrls) partHasUrls = false;

  const baseHasF = baseHasFiles(base);
  const baseHasU = baseHasUrls(base);

  if (partHasFiles) {
    const shouldTakeFileModality = !baseHasF || baseHasU;
    if (shouldTakeFileModality) {
      clearUrlFields(base);
      delete base.api_files;
      delete base.files;
      delete base.file_key;
      delete base.api_file_key;
      delete base.collection_s3_filename;
      if (f) base.api_files = f;
      else if (legacyFiles) base.files = legacyFiles;
      else if (fk) base.file_key = fk;
    } else {
      if (!base.api_files?.length && f) base.api_files = f;
      if (!Array.isArray(base.files) && legacyFiles) base.files = legacyFiles;
      if (!base.file_key && !base.collection_s3_filename && fk) base.file_key = fk;
    }
  }

  if (partHasUrls && !baseHasF) {
    if (!base.api_collection_urls?.length) {
      clearFileFields(base);
      if (urlArr?.length) base.api_collection_urls = urlArr;
      else {
        const one = urlOne || collectionUrlLegacy;
        if (one) base.api_collection_urls = [one];
      }
    }
  }

  const schema = part.schema_endpoint_url || part.schemaEndpointUrl;
  if (!base.schema_endpoint_url && schema) base.schema_endpoint_url = schema;

  if (!base.collection_original_filename && part.collection_original_filename) {
    base.collection_original_filename = part.collection_original_filename;
  }
}

export function mergeSessionApiConfigForActivitiesViewSetup(sessionDetails) {
  if (!sessionDetails || typeof sessionDetails !== 'object') return null;
  const b = {};
  const sd = sessionDetails;
  const sch = sd.schedule && typeof sd.schedule === 'object' ? sd.schedule : null;
  [
    sd.api_config,
    (sd.latest_session || sd.latestSession)?.api_config,
    sd.test_config,
    sd.testConfig,
    sch?.test_config,
    sch?.testConfig,
    {
      api_files: sd.api_files,
      apiFiles: sd.apiFiles,
      files: sd.files,
      api_collection_urls: sd.api_collection_urls,
      apiCollectionUrls: sd.apiCollectionUrls,
      file_key: sd.file_key ?? sd.api_file_key,
      api_file_key: sd.api_file_key,
      collection_s3_filename: sd.collection_s3_filename,
      collection_url: sd.collection_url,
      api_collection_url: sd.api_collection_url,
      collection_original_filename: sd.collection_original_filename,
      schema_endpoint_url: sd.schema_endpoint_url ?? sd.schemaEndpointUrl,
    },
  ].forEach((p) => mergeInto(b, p));
  const has =
    (Array.isArray(b.api_files) && b.api_files.length > 0) ||
    (Array.isArray(b.api_collection_urls) && b.api_collection_urls.length > 0) ||
    (Array.isArray(b.files) && b.files.length > 0) ||
    !!(b.file_key || b.collection_url || b.collection_s3_filename);
  return has ? b : null;
}

const EMPTY_ROW = {
  collectionFiles: null,
  collectionUrls: null,
  schemaEndpointUrl: null,
  collectionUrl: null,
  collectionFileKey: null,
  collectionFileName: null,
};

function toTargetRowPresentation(merged) {
  const m = merged || {};
  let collectionFiles = m.api_files || m.files || null;
  let collectionUrls = m.api_collection_urls || null;
  if (!collectionUrls?.length && m.collection_url?.trim()) {
    collectionUrls = [String(m.collection_url).trim()];
  }
  let collectionFileKey = m.file_key || m.collection_s3_filename || null;
  let collectionFileName = m.collection_original_filename || null;
  let collectionUrl = null;
  if (!collectionFiles?.length && collectionFileKey) {
    collectionFiles = [{ file_key: collectionFileKey, ...(collectionFileName ? { file_name: collectionFileName } : {}) }];
    collectionFileKey = null;
    collectionFileName = null;
  }
  if (!collectionFiles?.length && !collectionUrls?.length && m.collection_url?.trim()) {
    collectionUrl = String(m.collection_url).trim();
  }
  return {
    collectionFiles: collectionFiles?.length ? collectionFiles : null,
    collectionUrls: collectionUrls?.length ? collectionUrls : null,
    schemaEndpointUrl: m.schema_endpoint_url || null,
    collectionUrl,
    collectionFileKey,
    collectionFileName,
  };
}

/** Sidebar “Target Asset” collection line + schedule fallback when session omits files. */
export function getActivitiesCollectionPresentation(sessionDetails, linkedTarget, linkedCreated) {
  if (!sessionDetails) return { ...EMPTY_ROW };
  const first = toTargetRowPresentation(mergeSessionApiConfigForActivitiesViewSetup(sessionDetails));
  if (
    first.collectionFiles?.length ||
    first.collectionUrls?.length ||
    first.collectionUrl ||
    first.collectionFileKey
  ) {
    return first;
  }
  const tc = [linkedTarget?.test_config, linkedTarget?.testConfig, linkedCreated?.test_config, linkedCreated?.testConfig].find(
    (x) => x && typeof x === 'object'
  );
  return tc
    ? toTargetRowPresentation(mergeSessionApiConfigForActivitiesViewSetup({ ...sessionDetails, test_config: tc }))
    : first;
}
